import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, HelpCircle, Zap, DollarSign, Trophy, ShoppingCart, Users, Package } from 'lucide-react';

/*
 * DemoInteractiveV2 — the interactive query demo from the home page,
 * restyled to the V2 charte. Analysis logic preserved 1:1.
 * Rendered as the right column of the "data" section.
 */
export default function DemoInteractiveV2() {
  const { t, i18n } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);

  const mockData = {
    revenue: { thisMonth: 45230, lastMonth: 38450, growth: 17.6 },
    topProducts: [
      { name: 'T-shirt Premium Coton Bio', sales: 234, revenue: 7020 },
      { name: 'Jean Slim Fit Homme', sales: 189, revenue: 9450 },
      { name: 'Sneakers Urban Classic', sales: 156, revenue: 12480 },
      { name: 'Veste Légère Printemps', sales: 98, revenue: 6860 },
      { name: 'Casquette Logo Brodé', sales: 87, revenue: 1740 },
    ],
    averageCart: 67.5,
    newCustomers: 312,
    outOfStock: [
      { name: 'Pull Cachemire XL', stock: 0, demande: 'Haute' },
      { name: 'Chaussettes Pack x5 - 43/46', stock: 0, demande: 'Moyenne' },
      { name: 'Ceinture Cuir Marron M', stock: 2, demande: 'Haute' },
    ],
  };

  const formatCurrency = (amount) => {
    const locale = i18n.language === 'fr' ? 'fr-FR' : 'en-US';
    return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const analyzeQuery = (query) => {
    const q = query.toLowerCase();
    if (q.includes('chiffre') || q.includes('ca') || q.includes('revenue') || q.includes('affaires')) {
      const amount = formatCurrency(mockData.revenue.thisMonth);
      const lastMonth = formatCurrency(mockData.revenue.lastMonth);
      return {
        text: t('demo_interactive.responses.revenue', { amount, growth: mockData.revenue.growth, lastMonth }),
        table: {
          headers: [t('demo_interactive.table_headers.period'), t('demo_interactive.table_headers.revenue'), t('demo_interactive.table_headers.evolution')],
          rows: [
            [t('demo_interactive.table_rows.this_month'), amount, `<span class="highlight">+${mockData.revenue.growth}%</span>`],
            [t('demo_interactive.table_rows.last_month'), lastMonth, '-'],
          ],
        },
      };
    }
    if (q.includes('produit') || q.includes('top') || q.includes('vendu') || q.includes('product') || q.includes('selling')) {
      return {
        text: t('demo_interactive.responses.top_products'),
        table: {
          headers: [t('demo_interactive.table_headers.rank'), t('demo_interactive.table_headers.product'), t('demo_interactive.table_headers.sales'), t('demo_interactive.table_headers.revenue')],
          rows: mockData.topProducts.map((p, i) => [i + 1, p.name, p.sales, `<span class="highlight">${formatCurrency(p.revenue)}</span>`]),
        },
      };
    }
    if (q.includes('panier') || q.includes('moyen') || q.includes('cart') || q.includes('average')) {
      return { text: t('demo_interactive.responses.average_cart', { amount: formatCurrency(mockData.averageCart) }), table: null };
    }
    if (q.includes('client') || q.includes('nouveau') || q.includes('customer') || q.includes('new')) {
      return {
        text: t('demo_interactive.responses.new_customers', { count: mockData.newCustomers }),
        table: {
          headers: [t('demo_interactive.table_headers.source'), t('demo_interactive.table_headers.new_customers'), t('demo_interactive.table_headers.share')],
          rows: [
            [t('demo_interactive.table_rows.social_media'), '147', '<span class="highlight">47%</span>'],
            [t('demo_interactive.table_rows.google_organic'), '89', '29%'],
            [t('demo_interactive.table_rows.direct'), '51', '16%'],
            [t('demo_interactive.table_rows.email'), '25', '8%'],
          ],
        },
      };
    }
    if (q.includes('rupture') || q.includes('stock') || q.includes('out') || q.includes('available')) {
      return {
        text: t('demo_interactive.responses.out_of_stock'),
        table: {
          headers: [t('demo_interactive.table_headers.product'), t('demo_interactive.table_headers.stock'), t('demo_interactive.table_headers.demand')],
          rows: mockData.outOfStock.map((p) => [
            p.name,
            `<span class="${p.stock === 0 ? 'negative' : ''}">${p.stock}</span>`,
            p.demande === 'Haute' || p.demande === 'High' ? `<span class="negative">${t('demo_interactive.table_rows.high')}</span>` : p.demande,
          ]),
        },
      };
    }
    return { text: t('demo_interactive.responses.default'), table: null };
  };

  const handleQuery = (forcedQuery) => {
    const query = (typeof forcedQuery === 'string' ? forcedQuery : inputValue).trim();
    if (!query) return;
    setIsLoading(true);
    setResult(null);
    setTimeout(() => {
      setResult(analyzeQuery(query));
      setIsLoading(false);
    }, 1500);
  };

  const handleSuggestionClick = (query) => {
    setInputValue(query);
    handleQuery(query);
  };

  const handleKeyPress = (e) => { if (e.key === 'Enter') handleQuery(); };

  const suggestions = [
    { icon: <DollarSign />, text: t('demo_interactive.suggestions.revenue'), query: t('demo_interactive.queries.revenue') },
    { icon: <Trophy />, text: t('demo_interactive.suggestions.top_products'), query: t('demo_interactive.queries.top_products') },
    { icon: <ShoppingCart />, text: t('demo_interactive.suggestions.average_cart'), query: t('demo_interactive.queries.average_cart') },
    { icon: <Users />, text: t('demo_interactive.suggestions.new_customers'), query: t('demo_interactive.queries.new_customers') },
    { icon: <Package />, text: t('demo_interactive.suggestions.out_of_stock'), query: t('demo_interactive.queries.out_of_stock') },
  ];

  return (
    <section id="demo" className="v2-section">
      <div className="v2-container v2-demo-grid">
        {/* Left column — copy + suggestions */}
        <div className="v2-demo-copy v2-reveal">
          <span className="v2-pill">
            <Zap />
            {t('demo_interactive.badge')}
          </span>
          <h2>
            {t('demo_interactive.title')}
            <span className="v2-grad-text">{t('demo_interactive.title_span')}</span>
          </h2>
          <p>{t('demo_interactive.subtitle')}</p>
          <div className="v2-demo-suggestions">
            {suggestions.map((s, i) => (
              <button key={i} className="v2-demo-sugg" onClick={() => handleSuggestionClick(s.query)}>
                {s.icon}
                <span>{s.text}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Right column — interface */}
        <div className="v2d-interface v2-reveal">
          <div className="v2d-header">
            <div className="v2d-dots"><span /><span /><span /></div>
            <span className="v2d-title">Askeet Demo — askeet.ai</span>
          </div>
          <div className="v2d-body">
            <div className="v2d-inputwrap">
              <input
                className="v2d-input"
                type="text"
                placeholder={t('demo_interactive.placeholder')}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <button className="v2d-submit" onClick={() => handleQuery()} disabled={isLoading || !inputValue.trim()} aria-label="Send">
                <Send />
              </button>
            </div>

            <div className="v2d-result">
              {!result && !isLoading && (
                <div className="v2d-placeholder">
                  <HelpCircle />
                  <p>{t('demo_interactive.placeholder_text')}</p>
                </div>
              )}
              {isLoading && (
                <div className="v2d-loading">
                  <div className="v2d-spinner" />
                  <span>{t('demo_interactive.loading')}</span>
                </div>
              )}
              {result && !isLoading && (
                <>
                  <div className="v2d-response"><p>{result.text}</p></div>
                  {result.table && (
                    <div className="v2d-tablewrap">
                      <table className="v2d-table">
                        <thead>
                          <tr>{result.table.headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
                        </thead>
                        <tbody>
                          {result.table.rows.map((row, i) => (
                            <tr key={i}>{row.map((cell, j) => <td key={j} dangerouslySetInnerHTML={{ __html: cell }} />)}</tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
